export type ImageCompressionConfig = {
  maxBytes: number;
  maxDimension?: number;
  preserveTransparency?: boolean;
};

export type ImageDimensions = { width: number; height: number };

export function fitWithinDimensions(width: number, height: number, maxWidth: number, maxHeight: number): ImageDimensions {
  if (width <= 0 || height <= 0 || maxWidth <= 0 || maxHeight <= 0) return { width: 0, height: 0 };
  const scale = Math.min(maxWidth / width, maxHeight / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

export function fitImageDimensions(width: number, height: number, maxDimension = 2560): ImageDimensions {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 };
  const limit = Math.max(1, maxDimension);
  const scale = Math.min(1, limit / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('图片压缩失败')), 'image/webp', quality);
  });
}

function loadImage(file: File): Promise<{ image: HTMLImageElement; objectUrl: string }> {
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();
  return new Promise((resolve, reject) => {
    image.onload = () => resolve({ image, objectUrl });
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('无法读取图片，请换一张图片再试'));
    };
    image.src = objectUrl;
  });
}

export async function compressImagePreservingRatio(file: File, config: ImageCompressionConfig): Promise<File> {
  const { image, objectUrl } = await loadImage(file);
  try {
    let dimensions = fitImageDimensions(image.naturalWidth, image.naturalHeight, config.maxDimension || 2560);
    if (!dimensions.width || !dimensions.height) throw new Error('图片尺寸无效');
    const qualities = [0.92, 0.84, 0.76, 0.68, 0.6, 0.52, 0.44, 0.36];
    let lastBlob: Blob | null = null;

    for (let resizeAttempt = 0; resizeAttempt < 6; resizeAttempt += 1) {
      const canvas = document.createElement('canvas');
      canvas.width = dimensions.width;
      canvas.height = dimensions.height;
      const context = canvas.getContext('2d', { alpha: config.preserveTransparency === true });
      if (!context) throw new Error('浏览器不支持图片压缩');
      if (!config.preserveTransparency) {
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, dimensions.width, dimensions.height);
      } else {
        context.clearRect(0, 0, dimensions.width, dimensions.height);
      }
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(image, 0, 0, dimensions.width, dimensions.height);

      for (const quality of qualities) {
        lastBlob = await canvasToBlob(canvas, quality);
        if (lastBlob.size <= config.maxBytes) {
          const baseName = file.name.replace(/\.[^.]+$/, '').slice(0, 60) || 'image';
          return new File([lastBlob], `${baseName}-${dimensions.width}x${dimensions.height}.webp`, {
            type: 'image/webp',
            lastModified: Date.now(),
          });
        }
      }

      dimensions = {
        width: Math.max(1, Math.round(dimensions.width * 0.82)),
        height: Math.max(1, Math.round(dimensions.height * 0.82)),
      };
    }

    if (!lastBlob) throw new Error('图片压缩失败');
    throw new Error('图片压缩后仍然过大，请换一张图片再试');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
